import { createStore } from "@xstate/store";
import { produce } from "immer";

import { Logger } from "@moment/logging";

import { type Cell } from "~/models/cell";
import { type Page } from "~/models/page";
import { type CodeEditorState } from "~/state/types";

const logger = new Logger("code-editor.store");

const initialState: CodeEditorState = {
	open: false,
	tabs: {},
	tabDrafts: {},
	pinnedTabs: {},
	currentTab: {},
	flexTab: {},
};

export const codeEditorStore = createStore({
	context: initialState,
	on: {
		reset: () => initialState,

		openCodePane: (context, event: { open: boolean }) =>
			produce(context, (draft) => {
				draft.open = event.open;
			}),

		openTab: (
			context,
			event: {
				cellID: Cell["id"];
				activePageID?: Page["id"];
				openCodePane?: boolean;
				overridePin?: boolean;
			}
		) =>
			produce(context, (draft) => {
				const { cellID, activePageID, openCodePane, overridePin } = event;

				if (activePageID === undefined) {
					logger.warn("openTab: activePageID is undefined");
					return;
				}

				const pageTabs = draft.tabs[activePageID];
				const paneHasFlexTab = draft.flexTab[activePageID] !== undefined;
				const isNewTab = !pageTabs?.includes(cellID);
				const hasPinnedTab = draft.pinnedTabs[activePageID] !== undefined;

				/**
				 * if tab is new and code pane already has a flex tab
				 * close flex tab
				 */
				if (pageTabs && paneHasFlexTab && isNewTab) {
					const index = pageTabs.findIndex((id) => id === draft.flexTab[activePageID]);
					if (index !== -1) {
						draft.tabs[activePageID]?.splice(index, 1);
					}

					// Set the tab as current
					draft.currentTab[activePageID] = cellID;
				}

				/**
				 * if tab is new, push to tabs and set as flex tab
				 */
				if (isNewTab) {
					// if page has no tabs, initialize tabs array
					if (!draft.tabs[activePageID]) {
						draft.tabs[activePageID] = [];
					}

					draft.tabs[activePageID].push(cellID);
					draft.tabDrafts[cellID] = { code: undefined };

					draft.flexTab[activePageID] = cellID;
				}

				// Set the tab as current if there are no pinned tabs, or if overridePin is true
				if (!hasPinnedTab || overridePin) {
					draft.pinnedTabs[activePageID] = undefined;
					draft.currentTab[activePageID] = cellID;
				}

				if (openCodePane) {
					draft.open = true;
				}
			}),

		clearFlexTab: (context, event: { activePageID?: Page["id"] }) =>
			produce(context, (draft) => {
				const { activePageID } = event;
				if (activePageID === undefined) {
					logger.warn("clearFlexTab: activePageID is undefined");
					return;
				}

				draft.flexTab[activePageID] = undefined;
			}),

		closeTab: (context, event: { cellID: Cell["id"]; activePageID?: Page["id"] }) =>
			produce(context, (draft) => {
				const { cellID, activePageID } = event;
				if (activePageID === undefined) {
					logger.warn("closeTab: activePageID is undefined");
					return;
				}

				const pageTabs = draft.tabs[activePageID];
				const indexOf = pageTabs ? pageTabs.findIndex((s) => s === cellID) : -1;

				const tabDraft = draft.tabDrafts[cellID];

				if (tabDraft === undefined) {
					return;
				}

				tabDraft.code = undefined;

				// Remove from tabs list
				if (indexOf >= 0) {
					draft.tabs[activePageID]?.splice(indexOf, 1);
				}

				// Remove pinned tab
				draft.pinnedTabs[activePageID] = undefined;

				// open closest neighbor when tab is closed
				if (cellID === draft.currentTab[activePageID]) {
					const nextNeighbor = Math.max(indexOf - 1, 0);

					if (nextNeighbor >= 0 && pageTabs) {
						draft.currentTab[activePageID] = pageTabs[nextNeighbor];
					} else {
						draft.currentTab[activePageID] = undefined;
					}
				}

				// if closing last open tab, close editor
				if (draft.tabs[activePageID]?.length === 0) {
					draft.open = false;
				}
			}),

		togglePinTab: (context, event: { cellID: Cell["id"]; activePageID?: Page["id"] }) =>
			produce(context, (draft) => {
				const { cellID, activePageID } = event;

				if (activePageID === undefined) {
					logger.warn("togglePinTab: activePageID is undefined");
					return;
				}

				if (draft.pinnedTabs[activePageID] === cellID) {
					draft.pinnedTabs[activePageID] = undefined;
				} else {
					draft.pinnedTabs[activePageID] = cellID;
					draft.currentTab[activePageID] = cellID;
					draft.flexTab[activePageID] = undefined;
				}
			}),

		setTempCode: (context, event: { pageId: Page["id"]; cellId: Cell["id"]; code: string }) =>
			produce(context, (draft) => {
				const { pageId, cellId, code } = event;
				if (!cellId) {
					return;
				}

				draft.tabDrafts = Object.assign(draft.tabDrafts, { [cellId]: { code } });

				if (draft.flexTab[pageId] === cellId) {
					draft.flexTab[pageId] = undefined;
				}
			}),

		clearTempCode: (context, event: { cellID: Cell["id"] }) =>
			produce(context, (draft) => {
				const { cellID } = event;
				if (!cellID) {
					return;
				}

				const tabDraft = draft.tabDrafts[cellID];
				if (tabDraft === undefined) {
					return;
				}

				tabDraft.code = undefined;
			}),

		setTabs: (context, event: { tabs: Array<string>; activePageID?: Page["id"] }) =>
			produce(context, (draft) => {
				const { tabs, activePageID } = event;

				if (activePageID === undefined) {
					logger.warn("setTabs: activePageID is undefined");
					return;
				}

				const onlyContainsKnownIDs = tabs.every((id) =>
					draft.tabs[activePageID]?.includes(id)
				);

				// only set tabs if all IDs are known
				if (!onlyContainsKnownIDs) {
					return;
				}

				draft.tabs[activePageID] = tabs;
			}),
	},
});
